//Global imports
import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormLabel,
    CFormSwitch,
    CTab,
    CTabContent,
    CTabList,
    CTabPanel,
    CTabs,
} from '@coreui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

//File imports
import {
    APP_LANGAUGE,
    CREATE_DOCUMENT_PAGE_INFO,
    DOCUMENT_DETAIL_PAGE_INFO,
    LANGAUGE_NAME,
    PREVIEW_END,
} from '../../Constant';
import { Language } from '../../language/English';
import Notification from '../../services/Notification/Notification';
import { DocumentTemplateTranslationService } from './Api/DocumentTemplateTranslationService';
import Logger from '../../services/Logger/Logger';
import { DocumentTemplateTranslationPojo } from './Api/DocumentTemplateTranslationPojo';
import {
    API_DESCRIPTION_EDITOR_IMAGE_UPLOAD,
    DOCUMENT_TEMPLATE_TRANSLATION_STATUS_ACTIVE,
    DOCUMENT_TEMPLATE_TRANSLATION_STATUS_INACTIVE,
} from './Api/DocumentTemplateTranslationConstants';
import {
    manageFaqPosition,
    setInitialFaq,
    updateFaqIdWithNote,
} from './Components/FAQs/Redux/faqsSlice';
import {
    clearPreviewImage,
    setPositionPreviewImage,
} from './Components/UploadPreviewImages/Redux/previewImagesSlice';
import {
    clearTemplatePreviewImage,
    setPositionTemplatePreviewImage,
} from './Components/UploadPreviewImages/Redux/templatePreviewImagesSlice';
import { appDispatch, RootState } from '../../app/store';

//Component imports
import Loader from '../../components/Layout/Loader';
import { Layout } from '../../components/Layout';
import {
    CreateDocumentPage,
    DocumentDetailPage,
    DocumentTemplateText,
    DocumentTemplateOnlyPage,
} from './Tabs';
import StringUtils from '../../services/StringUtils/StringUtils';

interface FormErrors {
    documentTitle?: string;
    documentDetailUrl?: string;
    metaTitle?: string;
    metaKeyword?: string;
    metaDescription?: string;
    youtubeLink?: string;
    documentText?: string;
    documentTextPreviewRequired?: string;
    duplicatePlaceholders?: string;
    shortDescription?: string;
    description?: string;
    faqs?: string;
    sourceDocumentTemplateTranslationId?: string;
    documentDetailPageInfo?: {
        metaTitle?: string;
        metaKeyword?: string;
        metaDescription?: string;
        shortDescription?: string;
        bannerHeading?: string;
        description?: string;
        youtubeLink?: string;
        highlights?: string;
        benefits?: string;
    };
    createDocumentPageInfo?: {
        createDocumentUrl?: string;
        metaTitle?: string;
        metaKeyword?: string;
        metaDescription?: string;
        shortDescription?: string;
        pageHeading?: string;
        bannerHeading?: string;
    };
    [key: string]: any;
}

const DocumentTemplateTranslation = () => {
    const editor = useRef(null);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const location = useLocation();
    const { id, language, isEdit, name } = location.state ?? {};
    const [Editorconfig, setEditorconfig] = useState({
        height: 900,
    });
    const notification = new Notification();
    const stringUtils = new StringUtils();
    const dispatch = useDispatch<appDispatch>();
    const documentTemplateService = new DocumentTemplateTranslationService();
    const logger = new Logger();
    const faqs = useSelector((state: RootState) => state.faqsReducer.faqs);

    const previewImages = useSelector(
        (state: RootState) => state.previewImages.previewImages,
    );
    const templatePreviewImages = useSelector(
        (state: RootState) => state.templatePreviewImages.previewImages,
    );

    const [highlights, setHighlights] = useState([
        {
            key: 1,
            title: '',
            description: '',
        },
        {
            key: 2,
            title: '',
            description: '',
        },
        {
            key: 3,
            title: '',
            description: '',
        },
    ]);
    const [benefits, setBenefits] = useState([
        {
            key: 1,
            title: '',
            description: '',
        },
        {
            key: 2,
            title: '',
            description: '',
        },
        {
            key: 3,
            title: '',
            description: '',
        },
    ]);
    const [formData, setFormData] = useState<any>({
        documentTitle: '',
        documentDetailUrl: '',
        createDocumentUrl: '',
        documentText: '',
        shortDescription: '',
        specialInstruction: '',
        status: '',
        useOtherLanguageText: '',
        sourceDocumentTemplateTranslationId: '',
        documentDetailPageInfo: JSON.stringify({
            metaTitle: '',
            metaKeyword: '',
            metaDescription: '',
            bannerHeading: '',
            shortDescription: '',
            youtubeLink: '',
            description: '',
            highlights: highlights,
            benefits: benefits,
            faq: [],
            showHighlightOnDetailPage: true,
        }),
        createDocumentPageInfo: JSON.stringify({
            metaTitle: '',
            metaKeyword: '',
            metaDescription: '',
            pageHeading: '',
            bannerHeading: '',
            shortDescription: '',
        }),
        documentTemplatePdfContent: null,
        documentTemplatePreviewImages: [],
        documentTemplateTranslationId: null,
        hasDocumentTemplatePdf: false,
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [translationDataLoading, setTranslationDataLoading] =
        useState<boolean>(false);
    const [
        checkingOtherTranslationLoading,
        setCheckingOtherTranslationLoading,
    ] = useState<boolean>(false);
    const [translationData, setTranslationData] =
        useState<DocumentTemplateTranslationPojo | null>(null);
    const [folderName, setFolderName] = useState<string | null>(null);
    const [otherTranslationData, setOtherTranslationData] = useState<
        number[] | null
    >(null);
    const [isEditDocument, setIsEditDocument] = useState<boolean>(false);
    const [previewImageNote, setPreviewImageNote] = useState<boolean>(false);
    const [showFaqNote, setShowFaqNote] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<
        | 'document_template_text'
        | 'document_detail_page'
        | 'create_document_page'
        | 'document_template_only'
    >('document_template_text');

    useEffect(() => {
        setCheckingOtherTranslationLoading(true);
        dispatch(setInitialFaq());
        dispatch(clearPreviewImage());
        checkOtherTranslationData();
    }, []);

    const checkOtherTranslationData = async () => {
        try {
            let payload = {
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
            };

            const { success, message, data } =
                await documentTemplateService.isOtherTranslationExists(payload);

            if (success) {
                setOtherTranslationData(data);
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_OTHER_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setCheckingOtherTranslationLoading(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            setTranslationDataLoading(true);
            getDocumentTemplateTranslationData();
        }
    }, [isEdit]);

    useEffect(() => {
        const JoditEditorConfig = {
            height: 1000,
            readonly: false,
            askBeforePasteHTML: false,
            addNewLineOnDBLClick: false,
            enableDragAndDropFileToEditor: true,
            useSplitMode: false,
            paragraphize: false,
            cleanHTML: {
                fillEmptyParagraph: false,
            },
            uploader: {
                url: API_DESCRIPTION_EDITOR_IMAGE_UPLOAD,
                insertImageAsBase64URI: false,
                imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'webp'],
                headers: { Authorization: `Bearer ${user.token}` },
                filesVariableName: function (t: string) {
                    return 'files[' + t + ']';
                },
                withCredentials: false,
                pathVariableName: 'path',
                format: 'json',
                method: 'POST',
                prepareData: function (formData: any) {
                    setShowLoader(true);
                    formData.append('folderName', folderName);
                    return formData;
                },
                isSuccess: function (e: any) {
                    return e && e.response;
                },
                process: function (resp: any) {
                    return {
                        files: resp.response,
                    };
                },
                error: function (this: any, e: Error) {
                    this.j.e.fire('errorMessage', e.message, 'error', 4000);
                    setShowLoader(false);
                },
                defaultHandlerSuccess: function (this: any, resp: any) {
                    const j = this;
                    if (resp.files && resp.files.length) {
                        const tagName = 'img';
                        resp.files.forEach((data: any) => {
                            setFolderName(data.uniqueFolderName);
                            const elm = j.createInside.element(tagName);
                            elm.setAttribute('src', data.filePath);
                            j.s.insertImage(
                                elm as HTMLImageElement,
                                null,
                                j.o.imageDefaultWidth,
                            );
                        });
                    }
                    setShowLoader(false);
                },
                defaultHandlerError: function (this: any, e: { message: any }) {
                    this.j.e.fire('errorMessage', e.message);
                    setShowLoader(false);
                },
            },
        };

        setEditorconfig(JoditEditorConfig);
    }, [folderName, user.token]);

    const getDocumentTemplateTranslationData = async () => {
        try {
            let payload = {
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
            };

            const { success, message, data } =
                await documentTemplateService.get(payload);
            const {
                title,
                documentDetailUrl,
                createDocumentUrl,
                documentText,
                shortDescription,
                specialInstruction,
                folderName,
                status,
                sourceDocumentTranslationIdForText,
                documentDetailPageInfo,
                createDocumentPageInfo,
                documentTemplatePdfContent,
                documentTemplatePreviewImages,
                documentTemplateTranslationId,
                hasDocumentTemplatePdf,
            }: DocumentTemplateTranslationPojo = data;

            if (success) {
                setTranslationData(data);
                setFolderName(folderName);
                setFormData({
                    documentTitle: title,
                    documentDetailUrl,
                    createDocumentUrl,
                    documentText,
                    shortDescription,
                    specialInstruction,
                    status,
                    sourceDocumentTemplateTranslationId:
                        sourceDocumentTranslationIdForText,
                    useOtherLanguageText:
                        sourceDocumentTranslationIdForText != null
                            ? true
                            : false,
                    documentDetailPageInfo,
                    createDocumentPageInfo,
                    documentTemplatePdfContent,
                    documentTemplatePreviewImages,
                    documentTemplateTranslationId,
                    hasDocumentTemplatePdf,
                });
                let documentDetailPageInfoData = JSON.parse(
                    documentDetailPageInfo as any,
                );
                let faq = Array.isArray(documentDetailPageInfoData?.faq)
                    ? documentDetailPageInfoData?.faq
                    : JSON.parse(documentDetailPageInfoData?.faq ?? '[]');
                if (documentDetailPageInfoData && faq.length > 0) {
                    if (faq && Array.isArray(faq) && faq.length > 0) {
                        dispatch(manageFaqPosition(faq));
                    } else {
                        dispatch(manageFaqPosition([]));
                    }
                }

                let createDocumentPageInfoData = JSON.parse(
                    createDocumentPageInfo as any,
                );

                if (createDocumentPageInfoData) {
                    let previewBlurredImages = Array.isArray(
                        createDocumentPageInfoData?.previewBlurredImages,
                    )
                        ? createDocumentPageInfoData?.previewBlurredImages
                        : JSON.parse(
                              createDocumentPageInfoData?.previewBlurredImages ??
                                  '{}',
                          );

                    if (
                        previewBlurredImages &&
                        Array.isArray(previewBlurredImages) &&
                        previewBlurredImages.length > 0
                    ) {
                        dispatch(setPositionPreviewImage(previewBlurredImages));
                    } else {
                        dispatch(setPositionPreviewImage([]));
                    }
                }

                let documentTemplatePreviewImagesData = Array.isArray(
                    documentTemplatePreviewImages,
                )
                    ? documentTemplatePreviewImages
                    : JSON.parse(
                          (documentTemplatePreviewImages as any) ?? '[]',
                      );

                if (
                    documentTemplatePreviewImagesData &&
                    Array.isArray(documentTemplatePreviewImagesData) &&
                    documentTemplatePreviewImagesData.length > 0
                ) {
                    dispatch(
                        setPositionTemplatePreviewImage(
                            documentTemplatePreviewImagesData,
                        ),
                    );
                } else {
                    dispatch(setPositionTemplatePreviewImage([]));
                }

                setIsEditDocument(true);
                setPreviewImageNote(false);
                dispatch(updateFaqIdWithNote(null));
                setShowFaqNote(false);
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setTranslationDataLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        pageType: string,
    ) => {
        const { name, value } = e.target;
        let newValue = value;
        let page = pageType;
        let isUrlField =
            name ===
                stringUtils.convertToCamelCase(Language.CREATE_DOCUMENT_URL) ||
            name ===
                stringUtils.convertToCamelCase(Language.DOCUMENT_DETAIL_URL);

        if (isUrlField) {
            newValue = newValue.toLowerCase();
            page = '';
        }

        let newFormData = { ...formData, [name]: newValue };
        if (page) {
            let formDataObj = JSON.parse(formData[page] ?? '{}');
            let newFormDataObj = JSON.stringify({
                ...formDataObj,
                [name]: newValue,
            });
            newFormData = {
                ...formData,
                [page]: newFormDataObj,
            };
        }

        setFormData(newFormData);
        let errorPage = pageType;
        let newErrors = errorPage
            ? {
                  ...errors,
                  [errorPage]: { ...errors[errorPage], [name]: undefined },
              }
            : { ...errors, [name]: undefined };
        setErrors(newErrors);
    };

    const validateForm = (): boolean => {
        let newErrors: FormErrors = {};
        newErrors.documentDetailPageInfo = {};
        newErrors.createDocumentPageInfo = {};
        const regexForCheckingCommaSeperatedString =
            /^([A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)(,\s*[A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)*$/;
        const regexForCheckingUrl = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9-]+$/;
        // const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?$/;
        const youtubeRegex =
            /^https:\/\/www\.youtube\.com\/embed\/([a-zA-Z0-9_-]{11})$/;

        let isValid = true;
        let documentDetailPageInfo =
            typeof formData.documentDetailPageInfo === 'string'
                ? JSON.parse(formData.documentDetailPageInfo ?? '{}')
                : formData.documentDetailPageInfo;
        let createDocumentPageInfo = JSON.parse(
            formData.createDocumentPageInfo ?? '{}',
        );

        if (!formData.documentTitle.trim()) {
            newErrors.documentTitle = Language.DOCUMENT_TITLE_REQUIRED;
            setActiveTab('document_template_text');
            isValid = false;
        }

        // URL
        if (!formData.documentDetailUrl.trim()) {
            newErrors.documentDetailUrl = Language.URL_REQUIRED;
            setActiveTab('document_template_text');
            isValid = false;
        } else if (!regexForCheckingUrl.test(formData.documentDetailUrl)) {
            newErrors.documentDetailUrl =
                Language.URL_SHOULD_NOT_CONTAIN_WHITE_SPACE;
            setActiveTab('document_template_text');
            isValid = false;
        }

        // documentText
        if (formData.useOtherLanguageText) {
            if (
                !formData.sourceDocumentTemplateTranslationId ||
                isNaN(Number(formData.sourceDocumentTemplateTranslationId))
            ) {
                newErrors.sourceDocumentTemplateTranslationId =
                    Language.PLEASE_SELECT_VALID_DOCUMENT_TEMPLATE_TRANSLATION;
                setActiveTab('document_template_text');
                isValid = false;
            }
        } else {
            if (!formData.documentText.trim()) {
                newErrors.documentText = Language.DOCUMENT_TEXT_REQUIRED;
                setActiveTab('document_template_text');
                isValid = false;
            } else if (!formData.documentText.includes(PREVIEW_END)) {
                newErrors.documentTextPreviewRequired =
                    Language.PREVIEW_END_REQUIRED;
                setActiveTab('document_template_text');
                isValid = false;
            } else {
                const placeholders = [
                    ...formData.documentText.matchAll(/\[\[(.*?)\]\]/g),
                ].map((match) => match[1].toLowerCase());
                const duplicates = placeholders.filter(
                    (item, index) => placeholders.indexOf(item) !== index,
                );
                const uniqueDuplicates = Array.from(new Set(duplicates));
                if (uniqueDuplicates.length > 0) {
                    newErrors.duplicatePlaceholders =
                        Language.DUPLICATE_PLACEHOLDERS +
                        uniqueDuplicates.join(', ');
                    setActiveTab('document_template_text');
                    isValid = false;
                }
            }
        }

        // shortDescription
        if (!formData.shortDescription.trim()) {
            newErrors.shortDescription = Language.SHORT_DESCRIPTION_REQUIRED;
            setActiveTab('document_template_text');
            isValid = false;
        }

        if (
            documentDetailPageInfo &&
            documentDetailPageInfo.metaKeyword &&
            documentDetailPageInfo.metaKeyword.trim() != ''
        ) {
            if (
                !regexForCheckingCommaSeperatedString.test(
                    documentDetailPageInfo.metaKeyword.trim(),
                )
            ) {
                newErrors.documentDetailPageInfo.metaKeyword =
                    Language.KEYWORD_SHOULD_CONTAIN_COMMA_SEPERATED;
                setActiveTab('document_detail_page');
                isValid = false;
            }
        }

        // highlights;
        const isFilled = (value?: string) => Boolean(value?.trim());
        let highlights = documentDetailPageInfo?.highlights ?? [];
        const filledHighlightCount = highlights.filter(
            (h: any) => isFilled(h.title) && isFilled(h.description),
        ).length;

        const allHighlightsEmpty = highlights.every(
            (h: any) => !isFilled(h.title) && !isFilled(h.description),
        );

        const isValidHighlights =
            filledHighlightCount === highlights.length || allHighlightsEmpty;
        if (!isValidHighlights) {
            newErrors.documentDetailPageInfo.highlights =
                Language.ALL_HIGHLIGHTS_REQUIRED;
            setActiveTab('document_detail_page');
            isValid = false;
        }

        // benefits
        let benefits = documentDetailPageInfo?.benefits ?? [];
        const filledBenefitCount = benefits.filter(
            (h: any) => isFilled(h.title) && isFilled(h.description),
        ).length;

        const allBenefitsEmpty = benefits.every(
            (h: any) => !isFilled(h.title) && !isFilled(h.description),
        );
        const isValidBenefits =
            allBenefitsEmpty || filledBenefitCount === benefits.length;
        if (!isValidBenefits) {
            newErrors.documentDetailPageInfo.benefits =
                Language.ALL_BENEFITS_REQUIRED;
            setActiveTab('document_detail_page');
            isValid = false;
        }

        //description
        if (
            documentDetailPageInfo &&
            documentDetailPageInfo.description &&
            documentDetailPageInfo.description.trim() != ''
        ) {
            const requiredPlaceholders = ['cta_banner', 'document_steps'];
            const descriptionPlaceholders = [
                ...documentDetailPageInfo.description.matchAll(
                    /\[\[(.*?)\]\]/g,
                ),
            ].map((match) => match[1].toLowerCase());

            const counts: Record<string, number> = {};

            for (const placeholder of descriptionPlaceholders) {
                counts[placeholder] = (counts[placeholder] || 0) + 1;
            }

            const missingPlaceholders = requiredPlaceholders.filter(
                (placeholder) => !counts[placeholder],
            );
            if (missingPlaceholders.length > 0) {
                newErrors.documentDetailPageInfo.description =
                    Language.REQUIRED_PLACEHOLDER_MISSING +
                    ': ' +
                    missingPlaceholders
                        .map((p) => `[[${p.toUpperCase()}]]`)
                        .join(', ');
                setActiveTab('document_detail_page');
                isValid = false;
            }

            const duplicatedPlaceholders = Object.entries(counts)
                .filter(([_, count]) => count > 1)
                .map(([key]) => key);

            if (duplicatedPlaceholders.length > 0) {
                newErrors.documentDetailPageInfo.description =
                    Language.DUPLICATE_PLACEHOLDERS +
                    ': ' +
                    duplicatedPlaceholders
                        .map((p) => `[[${p.toUpperCase()}]]`)
                        .join(', ');
                setActiveTab('document_detail_page');
                isValid = false;
            }
        }

        //youtubeLink
        if (
            documentDetailPageInfo &&
            documentDetailPageInfo.youtubeLink &&
            documentDetailPageInfo.youtubeLink != ''
        ) {
            if (!youtubeRegex.test(documentDetailPageInfo.youtubeLink.trim())) {
                newErrors.documentDetailPageInfo.youtubeLink =
                    Language.YOUTUBE_LINK_NOT_VALID;
                setActiveTab('document_detail_page');
                isValid = false;
            }
        }

        //CREAT DOCUMENT PAGE URL
        if (!formData.createDocumentUrl.trim()) {
            newErrors.createDocumentPageInfo.createDocumentUrl =
                Language.URL_REQUIRED;
            setActiveTab('create_document_page');
            isValid = false;
        } else if (!regexForCheckingUrl.test(formData.createDocumentUrl)) {
            newErrors.createDocumentPageInfo.createDocumentUrl =
                Language.URL_SHOULD_NOT_CONTAIN_WHITE_SPACE;
            setActiveTab('create_document_page');
            isValid = false;
        }

        if (
            createDocumentPageInfo &&
            createDocumentPageInfo.metaKeyword &&
            createDocumentPageInfo?.metaKeyword.trim() != ''
        ) {
            if (
                !regexForCheckingCommaSeperatedString.test(
                    createDocumentPageInfo?.metaKeyword.trim(),
                )
            ) {
                newErrors.createDocumentPageInfo.metaKeyword =
                    Language.KEYWORD_SHOULD_CONTAIN_COMMA_SEPERATED;
                setActiveTab('create_document_page');
                isValid = false;
            }
        }

        Object.keys(newErrors).forEach((key, value) => {
            if (
                typeof newErrors[key] === 'object' &&
                Object.keys(newErrors[key]).length === 0
            ) {
                delete newErrors[key];
            }
        });

        setErrors(newErrors);
        let errorFields = Object.keys(newErrors);
        if (errorFields.length > 0) {
            if (errorFields[0] === DOCUMENT_DETAIL_PAGE_INFO) {
                errorFields = Object.keys(errors?.documentDetailPageInfo ?? {});
                if (errorFields[0] === 'highlights') {
                    errorFields = ['documentHighlights'];
                }
                if (errorFields[0] === 'benefits') {
                    errorFields = ['documentBenefits'];
                }
            }

            if (errorFields[0] === CREATE_DOCUMENT_PAGE_INFO) {
                errorFields = Object.keys(errors?.createDocumentPageInfo ?? {});
                if (errorFields[0] === 'createDocumentUrl') {
                    errorFields = ['createDocumentUrl'];
                }
            }

            const firstErrorElement = document.getElementsByName(
                errorFields[0],
            )[0];
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                firstErrorElement.focus();
            } else if (
                errorFields[0] === 'documentText' ||
                errorFields[0] === 'documentTextPreviewRequired' ||
                errorFields[0] === 'duplicatePlaceholders'
            ) {
                const editorElement = document.getElementById('documentText');
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            } else if (errorFields[0] === 'description') {
                const editorElement = document.getElementById('description');
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            } else if (
                errorFields[0] === 'sourceDocumentTemplateTranslationId'
            ) {
                const editorElement = document.getElementById(
                    'sourceDocumentTemplateTranslationId',
                );
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }

            return false;
        }

        return isValid;
    };

    const saveTranslation = async () => {
        setShowLoader(true);
        try {
            let payload: any = {
                ...formData,
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
                folderName,
                [DOCUMENT_DETAIL_PAGE_INFO]: {
                    ...JSON.parse(formData.documentDetailPageInfo),
                },
                [CREATE_DOCUMENT_PAGE_INFO]: {
                    ...JSON.parse(formData.createDocumentPageInfo),
                },
            };

            if (!payload.specialInstruction || payload.specialInstruction.trim() === '') {
                delete payload.specialInstruction;
            }
            if (faqs && Array.isArray(faqs) && faqs.length > 0) {
                let results: any[] = [];
                const isValidFaqs = faqs.every(
                    (faq) =>
                        typeof faq?.question === 'string' &&
                        typeof faq?.answer === 'string' &&
                        faq?.question.trim() !== '' &&
                        faq?.answer.trim() !== '',
                );

                if (isValidFaqs) {
                    results = faqs.map((faq) => {
                        if (faq?.image) {
                            return {
                                id: faq?.id,
                                question: faq?.question,
                                answer: faq?.answer,
                                image: faq?.fileName,
                                height: faq?.height,
                                width: faq?.width,
                                fileSizeInKb: faq?.fileSizeInKb,
                            };
                        } else {
                            if (
                                faq?.question.trim() != '' ||
                                faq?.answer.trim() != ''
                            ) {
                                return {
                                    id: faq?.id,
                                    question: faq?.question,
                                    answer: faq?.answer,
                                    image: null,
                                };
                            }
                        }
                    });
                }
                payload[DOCUMENT_DETAIL_PAGE_INFO]['faq'] = results;
            }

            let images = null;
            if (
                previewImages &&
                Array.isArray(previewImages) &&
                previewImages.length > 0
            ) {
                images = previewImages.map((img) => ({
                    image: img.image,
                    height: img.height,
                    width: img.width,
                    fileSizeInKb: img.fileSizeInKb,
                }));
            }
            payload[CREATE_DOCUMENT_PAGE_INFO]['previewBlurredImages'] = images;

            let templateImages = null;
            if (
                templatePreviewImages &&
                Array.isArray(templatePreviewImages) &&
                templatePreviewImages.length > 0
            ) {
                templateImages = templatePreviewImages.map((img) => ({
                    image: img.image,
                    height: img.height,
                    width: img.width,
                    fileSizeInKb: img.fileSizeInKb,
                }));
            }
            payload['documentTemplatePreviewImages'] = templateImages;
            const { success, message } =
                await documentTemplateService.create(payload);
            if (success) {
                notification.success(message);
                // resetFieldsAndGoBack();
                getDocumentTemplateTranslationData();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const updateTranslation = async () => {
        setShowLoader(true);
        try {
            let payload = {
                ...formData,
                documentTemplateId: id,
                languageId: APP_LANGAUGE[language],
                documentTemplateTranslationId:
                    translationData?.documentTemplateTranslationId,
                folderName,
                [DOCUMENT_DETAIL_PAGE_INFO]: JSON.parse(
                    formData?.documentDetailPageInfo ?? '{}',
                ),
                [CREATE_DOCUMENT_PAGE_INFO]: JSON.parse(
                    formData?.createDocumentPageInfo ?? '{}',
                ),
            };

            if (!payload.specialInstruction || payload.specialInstruction.trim() === '') {
                delete payload.specialInstruction;
            }

            if (faqs && Array.isArray(faqs) && faqs.length > 0) {
                let results = faqs.map((faq) => {
                    if (faq.image) {
                        return {
                            id: faq?.id,
                            question: faq?.question,
                            answer: faq?.answer,
                            image: faq?.fileName,
                            height: faq?.height,
                            width: faq?.width,
                            fileSizeInKb: faq?.fileSizeInKb,
                        };
                    } else {
                        return {
                            id: faq?.id,
                            question: faq?.question,
                            answer: faq?.answer,
                            image: null,
                        };
                    }
                });
                payload[DOCUMENT_DETAIL_PAGE_INFO]['faq'] = results;
            }

            let images = null;
            if (
                previewImages &&
                Array.isArray(previewImages) &&
                previewImages.length > 0
            ) {
                images = previewImages.map((img) => ({
                    image: img.image,
                    height: img.height,
                    width: img.width,
                    fileSizeInKb: img.fileSizeInKb,
                }));
            }

            payload[CREATE_DOCUMENT_PAGE_INFO]['previewBlurredImages'] = images;

            let templateImages = null;
            if (
                templatePreviewImages &&
                Array.isArray(templatePreviewImages) &&
                templatePreviewImages.length > 0
            ) {
                templateImages = templatePreviewImages.map((img) => ({
                    image: img.image,
                    height: img.height,
                    width: img.width,
                    fileSizeInKb: img.fileSizeInKb,
                }));
            }
            payload['documentTemplatePreviewImages'] = templateImages;
            const { success, message } =
                await documentTemplateService.update(payload);
            if (success) {
                notification.success(message);
                // resetFieldsAndGoBack();
                getDocumentTemplateTranslationData();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        if (!validateForm()) return;

        if (isEditDocument) {
            updateTranslation();
        } else {
            saveTranslation();
        }
    };

    const _checkForUrl = async (pageType?: string) => {
        let existingUrl = translationData?.documentDetailUrl;
        let newUrl = formData.documentDetailUrl;

        if (pageType === CREATE_DOCUMENT_PAGE_INFO) {
            existingUrl = translationData?.createDocumentUrl;
            newUrl = formData?.createDocumentUrl;
        }

        if (existingUrl === newUrl || newUrl === '') {
            return true;
        } else {
            try {
                const { success, message } =
                    await documentTemplateService.isUrlExists(newUrl);
                if (success) {
                    const newErrors: FormErrors = {};
                    if (pageType === CREATE_DOCUMENT_PAGE_INFO) {
                        let existingError = newErrors[pageType] ?? {};
                        newErrors[pageType] = { ...existingError };
                        newErrors[pageType]['createDocumentUrl'] = message;
                    } else {
                        newErrors.documentDetailUrl = message;
                    }
                    setErrors(newErrors);
                }
            } catch (error) {
                notification.error(Language.SOMETHING_IS_WRONG);
                logger.error(error);
            }
        }
    };

    const changeDocumentTemplateTranslationStatus = async (status: number) => {
        try {
            if (translationData?.documentTemplateTranslationId) {
                const documentTemplateService =
                    new DocumentTemplateTranslationService();
                let response = await documentTemplateService.delete(
                    translationData?.documentTemplateTranslationId,
                    status,
                );
                if (response) {
                    if (status === 1) {
                        notification.success(
                            Language.DOCUMENT_ACTIVED_SUCCESSFULLY,
                        );
                    } else {
                        notification.success(
                            Language.DOCUMENT_INACTIVED_SUCCESSFULLY,
                        );
                    }
                    getDocumentTemplateTranslationData();
                } else {
                    notification.error(
                        Language.CANNOT_CHANGE_DOCUMENT_TEMPLATES_TRANSLATION_STATUS_ERROR,
                    );
                }
            } else {
                notification.error(Language.UNABLE_TO_FETCH_TRANSLATION_DATA);
            }
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(e);
        }
    };

    const _onKeyDown = (e: { key: string; preventDefault: () => void }) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handlePaste = (
        e: React.ClipboardEvent<HTMLTextAreaElement>,
        pageType: string,
    ) => {
        const target = e.target as HTMLTextAreaElement;
        const { name, value } = target;
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const clean = pasted.replace(/\r?\n|\r/g, ' ');
        let data = {
            ...formData,
            [name]: (formData[name] || '') + clean,
        };
        if (pageType) {
            let formDataObj = JSON.parse(formData[pageType] ?? '{}');
            let newFormDataObj = JSON.stringify({
                ...formDataObj,
                [name]: formDataObj[name] + clean,
            });
            data = {
                ...formData,
                [pageType]: newFormDataObj,
            };
        }
        setFormData(data);
    };

    const updateHighlightContent = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: 'title' | 'description',
        index: number,
    ) => {
        const { name, value } = e.target;
        const documentDetailPageInfo = JSON.parse(
            formData?.documentDetailPageInfo ?? '{}',
        );
        const updatedHighlights = [...documentDetailPageInfo?.highlights];
        updatedHighlights[index] = {
            ...updatedHighlights[index],
            [field]: value,
        };

        let newHighlights = {
            ...documentDetailPageInfo,
            highlights: updatedHighlights,
        };
        let newFormData = {
            ...formData,
            documentDetailPageInfo: JSON.stringify(newHighlights),
        };

        setFormData(newFormData);
    };

    const updateBenefitContent = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: 'title' | 'description',
        index: number,
    ) => {
        const { name, value } = e.target;
        const documentDetailPageInfo = JSON.parse(
            formData?.documentDetailPageInfo ?? '{}',
        );
        const updatedBenefits = [...documentDetailPageInfo?.benefits];
        updatedBenefits[index] = {
            ...updatedBenefits[index],
            [field]: value,
        };

        let newBenefits = {
            ...documentDetailPageInfo,
            benefits: updatedBenefits,
        };
        let newFormData = {
            ...formData,
            documentDetailPageInfo: JSON.stringify(newBenefits),
        };

        setFormData(newFormData);
    };

    return (
        <Layout>
            {!translationDataLoading && (
                <>
                    {showLoader && <Loader />}
                    <CCard className="mb-4">
                        <CCardHeader>
                            <strong>
                                {name} / {LANGAUGE_NAME[language]}
                            </strong>
                            <div className="d-flex align-items-center gap-2">
                                <CFormLabel
                                    htmlFor="statusToggle"
                                    className={`mb-0 ${formData.status === 0 ? 'text-dark' : 'text-muted'}`}
                                >
                                    {Language.INACTIVE}
                                </CFormLabel>
                                <CFormSwitch
                                    id="statusToggle"
                                    name="status"
                                    className="mt-1"
                                    checked={formData.status === 1}
                                    onChange={(e) =>
                                        changeDocumentTemplateTranslationStatus(
                                            e.target.checked
                                                ? DOCUMENT_TEMPLATE_TRANSLATION_STATUS_ACTIVE
                                                : DOCUMENT_TEMPLATE_TRANSLATION_STATUS_INACTIVE,
                                        )
                                    }
                                    label=""
                                />
                                <CFormLabel
                                    htmlFor="statusToggle"
                                    className={`mb-0 ${formData.status === 1 ? 'text-dark' : 'text-muted'}`}
                                >
                                    {Language.ACTIVE}
                                </CFormLabel>
                            </div>
                        </CCardHeader>
                        <CCardBody>
                            <CForm>
                                <CTabs
                                    key={activeTab}
                                    activeItemKey={activeTab}
                                    onChange={(key) => {
                                        setActiveTab(key as any);
                                    }}
                                    className="mb-4"
                                >
                                    <CTabList
                                        variant="tabs"
                                        className="border-bottom"
                                    >
                                        <CTab itemKey="document_template_text">
                                            {Language.DOCUMENT_TEMPLATE_TEXT}
                                        </CTab>
                                        <CTab itemKey="document_detail_page">
                                            {Language.DOCUMENT_DETAIL_PAGE}
                                        </CTab>
                                        <CTab itemKey="create_document_page">
                                            {Language.CREATE_DOCUMENT_PAGE}
                                        </CTab>
                                        <CTab
                                            className="bg-white"
                                            aria-controls="document_template_only"
                                            itemKey="document_template_only"
                                        >
                                            {
                                                Language.DOWNLOAD_DOCUMENT_TEMPLATE_ONLY_SETTINGS
                                            }
                                        </CTab>
                                    </CTabList>

                                    <CTabContent className="pt-2">
                                        <CTabPanel itemKey="document_template_text">
                                            <DocumentTemplateText
                                                stringUtils={stringUtils}
                                                editor={editor}
                                                editorConfig={Editorconfig}
                                                name={name}
                                                data={{
                                                    documentTitle:
                                                        formData.documentTitle,
                                                    otherTranslationData:
                                                        otherTranslationData,
                                                    sourceDocumentTemplateTranslationId:
                                                        formData.sourceDocumentTemplateTranslationId,
                                                    useOtherLanguageText:
                                                        formData.useOtherLanguageText,
                                                    documentText:
                                                        formData.documentText,
                                                    documentDetailUrl:
                                                        formData.documentDetailUrl,
                                                    shortDescription:
                                                        formData.shortDescription,
                                                    specialInstruction:
                                                        formData.specialInstruction,
                                                }}
                                                errors={errors}
                                                language={language}
                                                useOtherLanguageCheckBoxChanged={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                ) => {
                                                    setFormData({
                                                        ...formData,
                                                        useOtherLanguageText:
                                                            e.target.checked,
                                                        sourceDocumentTemplateTranslationId:
                                                            e.target.checked
                                                                ? formData.sourceDocumentTemplateTranslationId
                                                                : '',
                                                    });
                                                    dispatch(
                                                        clearPreviewImage(),
                                                    );
                                                }}
                                                onLanguageSelectChange={(
                                                    e: React.ChangeEvent<HTMLSelectElement>,
                                                ) => {
                                                    setFormData({
                                                        ...formData,
                                                        sourceDocumentTemplateTranslationId:
                                                            e.target.value
                                                                ? parseInt(
                                                                      e.target
                                                                          .value,
                                                                      10,
                                                                  )
                                                                : null,
                                                    });
                                                }}
                                                onChageDocumentText={(
                                                    newContent: string,
                                                ) => {
                                                    setFormData(
                                                        (prev: FormData) => ({
                                                            ...prev,
                                                            documentText:
                                                                newContent,
                                                        }),
                                                    );
                                                }}
                                                handleChange={(
                                                    e: React.ChangeEvent<
                                                        | HTMLInputElement
                                                        | HTMLTextAreaElement
                                                    >,
                                                ) => handleChange(e, '')}
                                                onBlurUrlField={() =>
                                                    _checkForUrl('')
                                                }
                                                handlePaste={(e) =>
                                                    handlePaste(e, '')
                                                }
                                                onKeyDown={_onKeyDown}
                                            />
                                        </CTabPanel>
                                        <CTabPanel itemKey="document_detail_page">
                                            <DocumentDetailPage
                                                stringUtils={stringUtils}
                                                editorConfig={Editorconfig}
                                                folderName={folderName}
                                                editor={editor}
                                                showFaqNote={showFaqNote}
                                                name={name}
                                                data={
                                                    typeof formData?.documentDetailPageInfo ===
                                                    'string'
                                                        ? JSON.parse(
                                                              formData?.documentDetailPageInfo ??
                                                                  '{}',
                                                          )
                                                        : formData?.documentDetailPageInfo
                                                }
                                                errors={
                                                    errors?.documentDetailPageInfo ??
                                                    {}
                                                }
                                                handleChange={(
                                                    e: React.ChangeEvent<
                                                        | HTMLInputElement
                                                        | HTMLTextAreaElement
                                                    >,
                                                ) =>
                                                    handleChange(
                                                        e,
                                                        DOCUMENT_DETAIL_PAGE_INFO,
                                                    )
                                                }
                                                handleHightLightChange={(
                                                    e,
                                                    field,
                                                    index,
                                                ) => {
                                                    updateHighlightContent(
                                                        e,
                                                        field,
                                                        index,
                                                    );
                                                }}
                                                handleBenefitChange={(
                                                    e,
                                                    field,
                                                    index,
                                                ) => {
                                                    updateBenefitContent(
                                                        e,
                                                        field,
                                                        index,
                                                    );
                                                }}
                                                onBlurUrlField={() =>
                                                    _checkForUrl(
                                                        DOCUMENT_DETAIL_PAGE_INFO,
                                                    )
                                                }
                                                handlePaste={(e) =>
                                                    handlePaste(
                                                        e,
                                                        DOCUMENT_DETAIL_PAGE_INFO,
                                                    )
                                                }
                                                onKeyDown={_onKeyDown}
                                                onChageDescriptionText={(
                                                    newContent: string,
                                                ) => {
                                                    let documentDetailPageInfo =
                                                        JSON.parse(
                                                            formData.documentDetailPageInfo ??
                                                                '{}',
                                                        );
                                                    let newDocumentDetailPageInfo =
                                                        {
                                                            ...documentDetailPageInfo,
                                                            description:
                                                                newContent,
                                                        };
                                                    let newFormData = {
                                                        ...formData,
                                                        documentDetailPageInfo:
                                                            JSON.stringify(
                                                                newDocumentDetailPageInfo,
                                                            ),
                                                    };
                                                    setFormData(newFormData);
                                                }}
                                                handleShowHighlightOnDetailPage={(
                                                    checked: boolean,
                                                ) => {
                                                    let documentDetailPageInfo =
                                                        JSON.parse(
                                                            formData.documentDetailPageInfo ??
                                                                '{}',
                                                        );
                                                    let newDocumentDetailPageInfo =
                                                        {
                                                            ...documentDetailPageInfo,
                                                            showHighlightOnDetailPage:
                                                                checked,
                                                        };
                                                    let newFormData = {
                                                        ...formData,
                                                        documentDetailPageInfo:
                                                            JSON.stringify(
                                                                newDocumentDetailPageInfo,
                                                            ),
                                                    };
                                                    setFormData(newFormData);
                                                }}
                                                setLoader={(status: boolean) =>
                                                    setShowLoader(status)
                                                }
                                                setUniqueFolderName={(
                                                    name: string,
                                                ) => setFolderName(name)}
                                                setShowFaqNote={(
                                                    status: boolean,
                                                ) => setShowFaqNote(status)}
                                            />
                                        </CTabPanel>
                                        <CTabPanel itemKey="create_document_page">
                                            <CreateDocumentPage
                                                stringUtils={stringUtils}
                                                name={name}
                                                folderName={folderName}
                                                previewImageNote={previewImageNote}
                                                data={
                                                    typeof formData?.createDocumentPageInfo ===
                                                    'string'
                                                        ? {
                                                              ...JSON.parse(
                                                                  formData?.createDocumentPageInfo ??
                                                                      '{}',
                                                              ),
                                                              createDocumentUrl:
                                                                  formData?.createDocumentUrl,
                                                          }
                                                        : {
                                                              ...formData?.createDocumentPageInfo,
                                                              createDocumentUrl:
                                                                  formData?.createDocumentUrl,
                                                          }
                                                }
                                                errors={
                                                    errors?.createDocumentPageInfo ??
                                                    {}
                                                }
                                                handleChange={(
                                                    e: React.ChangeEvent<
                                                        | HTMLInputElement
                                                        | HTMLTextAreaElement
                                                    >,
                                                ) =>
                                                    handleChange(
                                                        e,
                                                        CREATE_DOCUMENT_PAGE_INFO,
                                                    )
                                                }
                                                onBlurUrlField={() =>
                                                    _checkForUrl(
                                                        CREATE_DOCUMENT_PAGE_INFO,
                                                    )
                                                }
                                                handlePaste={(e) =>
                                                    handlePaste(
                                                        e,
                                                        CREATE_DOCUMENT_PAGE_INFO,
                                                    )
                                                }
                                                onKeyDown={_onKeyDown}
                                                setNote={(status: boolean) => {
                                                    setPreviewImageNote(status);
                                                }}
                                                setLoader={(status: boolean) =>
                                                    setShowLoader(status)
                                                }
                                                setFolder={(name: string) =>
                                                    setFolderName(name)
                                                }
                                            />
                                        </CTabPanel>
                                        <CTabPanel
                                            className="p-3"
                                            itemKey="document_template_only"
                                        >
                                            <DocumentTemplateOnlyPage
                                                formData={formData}
                                                setFormData={setFormData}
                                                setFolder={(name: string) =>
                                                    setFolderName(name)
                                                }
                                                setLoader={(status: boolean) =>
                                                    setShowLoader(status)
                                                }
                                                setNote={(status: boolean) =>
                                                    setPreviewImageNote(status)
                                                }
                                                stringUtils={stringUtils}
                                                folderName={folderName}
                                                previewImageNote={
                                                    previewImageNote
                                                }
                                            />
                                        </CTabPanel>
                                    </CTabContent>
                                </CTabs>

                                <div className="d-flex justify-content-end">
                                    <CButton
                                        color="success"
                                        type="submit"
                                        className="mt-4"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSubmit(e as any);
                                        }}
                                    >
                                        {isEditDocument
                                            ? Language.UPDATE_DOCUMENT
                                            : Language.SAVE_DOCUMENT}
                                    </CButton>
                                </div>
                            </CForm>
                        </CCardBody>
                    </CCard>
                </>
            )}
        </Layout>
    );
};

export default DocumentTemplateTranslation;
